import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchInventoryDates, fetchInventoryUsers } from '../utils/api';
import { InventoryDateSummary, InventoryUserEntry } from '../types/inventory';
import { queryKeys } from '../lib/queryClient';

const sortDatesDesc = (dates: InventoryDateSummary[] = []) =>
  [...dates].sort((a, b) => b.date.localeCompare(a.date));

const sortUsersAsc = (users: InventoryUserEntry[] = []) =>
  [...users].sort((a, b) => {
    const idA = a.user_id || '';
    const idB = b.user_id || '';
    return idA.localeCompare(idB, undefined, { numeric: true, sensitivity: 'base' });
  });

export const useInventoryDates = () => {
  const query = useQuery({
    queryKey: queryKeys.inventoryDates(),
    queryFn: fetchInventoryDates,
    staleTime: 5 * 60 * 1000,
  });

  const dates = useMemo(() => sortDatesDesc(query.data?.dates), [query.data]);

  return {
    ...query,
    dates,
    generatedAt: query.data?.generated_at,
    rawDates: query.data?.dates,
  };
};

export const useInventoryUsers = (date: string | null) => {
  const query = useQuery({
    queryKey: queryKeys.inventoryUsers(date || 'none'),
    queryFn: () => fetchInventoryUsers(date as string),
    enabled: Boolean(date),
    staleTime: 2 * 60 * 1000,
  });

  const entries = useMemo(() => sortUsersAsc(query.data?.entries), [query.data]);

  return {
    ...query,
    entries,
    generatedAt: query.data?.generated_at,
    artifactTypes: query.data?.artifact_types,
    sourceRoot: query.data?.source_root,
    count: query.data?.count ?? entries.length,
  };
};
